import { FPMSyncType } from '../../common/enums';

export enum DrawingObjectType {
    circle = 0,
    rectangle = 1,
    polygon = 2
}

export enum DrawingObjectLineType {
    solid = 0,
    dash = 1,
    dotted = 2,
    none = 3
}
export enum DrawingObjectLabelAlignment {
    left = 0,
    center = 1,
    right = 2,
    perimeter = 3
}

export enum DrawingObjectFillStyle {
    none = 0,
    solid = 1,
    verticalHatch = 2,
    horizontalHatch = 3,
    crossHatch = 4,
    diagHatchLeft = 5,
    diagHatchRight = 6,
    diagCrossHatch = 7
}

export enum DrawingObjectCenterStyle {
    none = 0,
    dot = 1,
    plus = 2,
    x = 3,
    hollowCircle = 4,
    filledCircle = 5
}

export enum DrawingObjectCategory {
    viewBased = 0,
    alertBased = 1,
    avoidance = 2
}

export enum DrawingObjectOffsetUnits {
    miles = 0,
    degrees = 1
}

export enum DrawingObjectCenterUnits {
    latLng = 0,
    pixel = 1
}

export interface DrawingObjectLabelSettings {
    text: string;
    alignment: DrawingObjectLabelAlignment;
    color: string;
}

export interface DrawingObjectLineSettings {
    style: DrawingObjectLineType;
    width: number;
    color: string;
}

export interface DrawingObjectFillSettings {
    color: string;
    style: DrawingObjectFillStyle;
}

export interface DrawingObjectCenterSettings {
    style: DrawingObjectCenterStyle;
    color: string;
    size: number;
}

export interface DrawingObjectZoomSettings {
    object: number;
    point: number;
    label: number;
}

export interface DrawingObjectAltitudeRange {
    min: number | null;
    max: number | null;
}

export interface DrawingObjectAlerts {
    plane: boolean;
    route: boolean;
}

export interface DrawingObjectsFPMProperties {
    sync: boolean;
    FPMSyncType: FPMSyncType;
}

export interface DrawingObjectValidTimeSettings {
    startDay: string | null;
    endDay: string | null;
    startTime: string | null; // HH:mm:ss format
    endTime: string | null; // HH:mm:ss format
    activeDaysOfWeek: number; // Bitmask representing active days of the week (0-127)
    greyOutNonActive: boolean;
}

export interface DrawingObjectPolygonGeometry {
    polygonCoordinates: number[][];
}

export interface DrawingObjectRectangleGeometry {
    centerUnits: DrawingObjectCenterUnits;
    centerX: number;
    centerY: number;
    sizeUnits: DrawingObjectOffsetUnits;
    width: number;
    height: number;
}

export interface DrawingObjectCircleGeometry {
    centerUnits: DrawingObjectCenterUnits;
    centerX: number;
    centerY: number;
    radiusUnits: DrawingObjectOffsetUnits;
    radiusX: number;
    radiusY: number;
}

export interface DrawingObjectBase {
    id: string;
    name: string;
    isCompany: boolean;
    label: DrawingObjectLabelSettings;
    line: DrawingObjectLineSettings;
    fill: DrawingObjectFillSettings;
    center: DrawingObjectCenterSettings;
    zoom: DrawingObjectZoomSettings;
    category: DrawingObjectCategory;
    altitude: DrawingObjectAltitudeRange;
    alerts: DrawingObjectAlerts;
    fpmProperties: DrawingObjectsFPMProperties;
    validTime: DrawingObjectValidTimeSettings;
}

export interface DrawingObjectCircle extends DrawingObjectBase {
    type: DrawingObjectType.circle;
    geometry: DrawingObjectCircleGeometry;
}

export interface DrawingObjectPolygon extends DrawingObjectBase {
    type: DrawingObjectType.polygon;
    geometry: DrawingObjectPolygonGeometry;
}

export interface DrawingObjectRectangle extends DrawingObjectBase {
    type: DrawingObjectType.rectangle;
    geometry: DrawingObjectRectangleGeometry;
}

export type DrawingObject = DrawingObjectCircle | DrawingObjectPolygon | DrawingObjectRectangle;
export type DrawingObjectGeometry = DrawingObjectCircleGeometry | DrawingObjectPolygonGeometry | DrawingObjectRectangleGeometry;

export interface DrawingObjectLayerSettings {
    show: boolean;
    avoidance: boolean;
    alert: boolean;
    view: boolean;
    viewObjectIds: string[];
}

export interface HoldingAnalyzerFlight {
    id: number;
    acid: string;
    origin: string | null;
    destination: string | null;
    eta: string | null;
    etr: string | null;
    altitude: string | null;
    holding: boolean;
}

export type DrawingObjectUpdateDiff = {
    updated: DrawingObject[];
    deleted: string[];
    added: DrawingObject[];
    objectsToDisableFpmSync: string[];
};

export type DrawingObjectCreateInfo = (
    | { type: DrawingObjectType.circle; radius: number[]; center: number[] }
    | { type: DrawingObjectType.rectangle; center: number[]; width: number; height: number }
    | { type: DrawingObjectType.polygon; points: number[][] }
) & {
    name?: string;
    label?: { text: string };
    category?: DrawingObjectCategory;
    line?: { color: string };
    fpmProperties?: { sync?: boolean };
    fill?: { color?: string; style?: DrawingObjectFillStyle };
};
